import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiGet } from 'src/lib/api/axios';

import { PaginatedDocumentsResponse } from './types';
import { WikiDocument } from 'src/lib/types';

const initialState: {
  filterDocuments: {
    matching: WikiDocument[];
    matchingPage: number;
    matchingTotalPages: number;
    remaining: WikiDocument[];
    remainingPage: number;
    remainingTotalPages: number;
    isLoading: boolean;
  };
} = {
  filterDocuments: {
    matching: [],
    matchingPage: 0,
    matchingTotalPages: 0,
    remaining: [],
    remainingPage: 0,
    remainingTotalPages: 0,
    isLoading: false
  }
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setFilterDocumentsMatching: (
      state,
      {
        payload
      }: PayloadAction<{
        documents: WikiDocument[];
        page: number;
        totalPages: number;
        append: boolean;
      }>
    ) => {
      if (payload.append) {
        state.filterDocuments.matching = [...state.filterDocuments.matching, ...payload.documents];
      } else {
        state.filterDocuments.matching = payload.documents;
      }
      state.filterDocuments.matchingPage = payload.page;
      state.filterDocuments.matchingTotalPages = payload.totalPages;
    },

    setFilterDocumentsRemaining: (
      state,
      {
        payload
      }: PayloadAction<{
        documents: WikiDocument[];
        page: number;
        totalPages: number;
        append: boolean;
      }>
    ) => {
      if (payload.append) {
        state.filterDocuments.remaining = [...state.filterDocuments.remaining, ...payload.documents];
      } else {
        state.filterDocuments.remaining = payload.documents;
      }
      state.filterDocuments.remainingPage = payload.page;
      state.filterDocuments.remainingTotalPages = payload.totalPages;
    },

    setFilterDocumentsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.filterDocuments.isLoading = payload;
    }
  }
});

// Reducer
export default slice.reducer;

export function fetchFilterDocuments(params: {
  categories?: string[];
  domains?: string[];
  subdomains?: string[];
  search?: string;
  page: number;
  append?: boolean;
}): (dispatch: any) => Promise<void> {
  return async (dispatch) => {
    dispatch(slice.actions.setFilterDocumentsLoading(true));

    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', params.page.toString());
      if (params.categories?.length) {
        params.categories.forEach((c) => queryParams.append('categories', c));
      }
      if (params.domains?.length) {
        params.domains.forEach((d) => queryParams.append('domains', d));
      }
      if (params.subdomains?.length) {
        params.subdomains.forEach((s) => queryParams.append('subdomains', s));
      }
      if (params.search) {
        queryParams.set('search', params.search);
      }

      const res = await apiGet<PaginatedDocumentsResponse>(`docs/list?${queryParams.toString()}`, {
        meta: {
          globalError: true,
          context: 'filter-documents-fetch',
          description: 'Failed to fetch documents. Please contact support if the problem persists.',
          title: 'Failed to load documents.'
        }
      });

      if (!params.append) {
        // Clear stale excluded documents from a previous filter set
        dispatch(
          slice.actions.setFilterDocumentsRemaining({
            documents: [],
            page: 0,
            totalPages: 0,
            append: false
          })
        );
      }

      dispatch(
        slice.actions.setFilterDocumentsMatching({
          documents: res.data.references,
          page: res.data.page,
          totalPages: res.data.total_pages,
          append: params.append ?? false
        })
      );
    } finally {
      dispatch(slice.actions.setFilterDocumentsLoading(false));
    }
  };
}

export function fetchRemainingDocuments(params: {
  categories?: string[];
  domains?: string[];
  subdomains?: string[];
  search?: string;
  page: number;
  append?: boolean;
}): (dispatch: any) => Promise<void> {
  return async (dispatch) => {
    dispatch(slice.actions.setFilterDocumentsLoading(true));

    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', params.page.toString());
      if (params.categories?.length) {
        params.categories.forEach((c) => queryParams.append('categories', c));
      }
      if (params.domains?.length) {
        params.domains.forEach((d) => queryParams.append('domains', d));
      }
      if (params.subdomains?.length) {
        params.subdomains.forEach((s) => queryParams.append('subdomains', s));
      }
      if (params.categories?.length || params.domains?.length || params.subdomains?.length) {
        queryParams.set('exclude', 'true');
      }
      if (params.search) {
        queryParams.set('search', params.search);
      }

      const res = await apiGet<PaginatedDocumentsResponse>(`docs/list?${queryParams.toString()}`, {
        meta: {
          globalError: true,
          context: 'remaining-documents-fetch',
          description: 'Failed to fetch remaining documents. Please contact support if the problem persists.',
          title: 'Failed to load documents.'
        }
      });

      dispatch(
        slice.actions.setFilterDocumentsRemaining({
          documents: res.data.references,
          page: res.data.page,
          totalPages: res.data.total_pages,
          append: params.append ?? false
        })
      );
    } finally {
      dispatch(slice.actions.setFilterDocumentsLoading(false));
    }
  };
}
